<template>
	<div>
		<!-- Displayed value -->
		<form-field-readonly
			@click="openModal"
			@remove:clicked="onUnselect"
			:hasRemove="computedValue ? true : false"
		>
			<!-- If no contact selected -->
			<div v-if="!computedValue"><slot name="empty">---</slot></div>
			<!-- If fact selected, handle partial data missing with displaying N/A -->
			<div v-else>
				<span>{{ computedValue.value ? computedValue.value : "N/A" }}</span>
				<span class="c6 ml1">
					({{ computedValue.type ? computedValue.type : "N/A" }})
				</span>
			</div>
		</form-field-readonly>

		<!-- Modal -->
		<core-modal :name="instanceKey" headline="Select Contact">
			<div class="p4">
				<!-- Entity name and crete new -->
				<div class="xs-12 flex wrap mb4">
					<!-- Headline -->
					<div class="xs-8">
						<span class="fw7">{{ entityName }}</span>
						{{ $t("has_these_contacts") }}:
					</div>
					<!-- Button -->
					<div class="flex xs-4 xs-right">
						<j-button type="tertiary" @click="$modal.show('contactCreate')">
							{{ $t("add_new_contact") }}
						</j-button>
					</div>
				</div>

				<!-- Selection -->
				<div v-if="computedList && computedList.length">
					<div
						v-for="item in computedList"
						:key="item.id"
						class="r2 p2 mb2 cu2 bg5"
						:class="[
							{ 'bc2 b2': item.isSelected },
							{ 'bc7 b1': !item.isSelected }
						]"
						@click="onSelect(item.id)"
					>
						<div class="lh125 c6 fw7 fs3">
							{{ item.type ? item.type : "N/A" }}
						</div>
						<div class="xs-12 lh125">{{ item.value ? item.value : "N/A" }}</div>
						<div class="xs-12 mt1" v-if="item.isSuggested">
							<j-tag size="small" variant="warning" label="Suggested" />
						</div>
					</div>
				</div>

				<!-- No data -->
				<div v-else class="xs-12 bg5 r2 b1 bc7 p4 ta2 fs3 c6 fw7 mb4">
					{{ $t("errors.no_data") }}
				</div>

				<!-- Add new modal -->
				<core-modal
					name="contactCreate"
					headline="Create Fact"
					:pivotX="0"
					:pivotY="0"
					:hasHeader="false"
				>
					<div style="height:100vh; overflow: scroll;" class="p4">
						<entity-contact-create
							:createOptions="createOptions"
							:entityId="entityId"
							:entityName="entityName"
							@contact:created="onContactCreated"
							@cancel="$modal.hide('contactCreate')"
						/>
					</div>
				</core-modal>
			</div>
		</core-modal>
	</div>
</template>

<script>
import CoreModal from "@/modules/core/components/CoreModal"
import JButton from "@/components/JButton"
import FormFieldReadonly from "../FormFieldReadonly.vue"
import EntityContactCreate from "./EntityContactCreate"
import JTag from "@/components/JTag"
import { getFieldData, shouldSuggest } from "../utils/index.js"

export default {
	components: {
		CoreModal,
		JButton,
		FormFieldReadonly,
		EntityContactCreate,
		JTag
	},

	props: {
		/**
		 * Value represents `id`
		 */
		value: {
			type: String
		},

		/**
		 * Field from schema
		 * It's an object that inlcudes numerous properties, read docs for more details
		 * We compute some values based on those properties
		 */
		field: {
			type: Object,
			default: () => ({})
		},

		/**
		 * Options for filtering suggested facts
		 */
		suggest: {
			type: Object
		},

		/**
		 * Instructions for creation of a new fact
		 * What properties it can included is in docs
		 */
		createOptions: {
			type: Object
		}
	},

	data() {
		return {
			instanceKey: Math.random().toString(), // get unique key fro modal window

			// Form for adding new contact
			isAdding: false,
			isAddingLoading: false,
			form: {
				type: "",
				value: ""
			}
		}
	},

	computed: {
		/**
		 * Entity object that we get from API
		 * We get some other properties from this object that are crucial for some operations
		 * e.g. entityId, entityName, entityType via __typename
		 * thus it has to contain properties: `__typename`, `id` `name`
		 */
		entity() {
			const entity = getFieldData("entity", this.field, this)
			return entity
		},

		/**
		 * Based on this we do API calls
		 */
		entityId() {
			return this.entity?.id
		},

		/**
		 * Based on this we do API calls
		 */
		entityType() {
			return this.entity?.__typename
		},

		/**
		 * When we need to display name of an entity
		 */
		entityName() {
			return this.entity?.name
		},

		/**
		 * Will return data based on relation field
		 */
		cache() {
			return getFieldData("relation", this.field, this)
		},

		/**
		 * What user can select from, displayed in UI
		 */
		computedList() {
			return this.cache
				?.map(item => ({
					...item,
					isSelected: item.id === this.value,
					isSuggested: shouldSuggest(item, this.suggest)
				}))
				.sort((a, b) => b.isSuggested - a.isSuggested) // sort based on suggested first
		},

		/**
		 * So that we do not display `id` but proper value
		 */
		computedValue() {
			return this?.cache?.find(item => item?.id === this.value) || null
		}
	},

	methods: {
		// When user wants to open a picker modal,
		// check if if we actually have `entity` to pick data for
		// If not, show message and don't show modal
		openModal() {
			if (!this.entity) {
				this.$store.dispatch("addMessage", {
					delay: 1500,
					message: {
						type: "neutral",
						text: "You have to select entity first"
					}
				})
				return
			}

			this.$modal.show(this.instanceKey)
		},

		// When user selects
		onSelect(id) {
			this.$modal.hide(this.instanceKey)
			this.$emit("input", id)
		},

		// When user unselects
		onUnselect() {
			this.$emit("input", "")
			this.$modal.hide(this.instanceKey)
		},

		async onContactCreated(id) {
			this.$modal.hide("contactCreate")

			// Update all entities in store by refetching the case graph
			await this.$store.dispatch("caseManagement/fetchCase", {
				caseId: this.$store.state.caseManagement.caseId
			})

			// Select contact automatically
			// Order is important, we do it after we update store
			this.onSelect(id)
		}
	}
}
</script>
